<svelte:options customElement={{ tag: "bt-curation-share-modal", shadow: "none" }} />

<script lang="ts">
  // Imports
  import { modalStore } from '$shared/modal/modalStore';
  import Modal from '$shared/modal/Modal.svelte';
  import Icon from '$shared/icons/Icon.svelte';

  // Props
  export let assignedDueDate: string = '';
  export let assignedGroupId: string = '';
  export let copyUrl: string = '';
  export let groupsCount: string = '0';
  export let orgName: string = '';

  // Translation props
  export let assignButtonText: string = 'Assign';
  export let assignToGroupText: string = 'Assign to group';
  export let assignedText: string = 'This curation is visible only to people in this group.';
  export let assignedGroupText: string = 'Only people in an assigned group will see this curation.';
  export let copyLinkButtonText: string = 'Copy link';
  export let copyLinkSubtitleText: string = 'Anyone with the link and Big Think+ access can view.';
  export let noGroupsText: string = 'Reach out to Partner Services at partnerservices@bigthink.com to set up groups for assignment.'
  export let prePublishSubtitleText: string = 'Everyone at %{org_name} will see this curation.';
  export let publishButtonText: string = 'Publish';
  export let publishSubtitleText: string = '`This curation is visible to everyone at %{org_name}.';
  export let publishText: string = 'Publish to everyone at %{org_name}';
  export let publishedBoolText: string = 'Unpublish this curation to assign to a group.';
  export let publishedState: string = 'false';
  export let unassignButtonText: string = 'Unassign';
  export let unassignUrl: string = '';
  export let unpublishButtonText: string = 'Unpublish';
  export let unpublishUrl: string = '';
  export let unassignedUnpublishedBadge: string = 'Unassigned/unpublished';
  export let unpublishConfirmTitle: string = 'Unpublish this curation?';
  export let warningLabel: string = 'Warning';
  export let unpublishConfirmWarningText: string = 'This will also cancel all scheduled email reminders for this curation.';
  export let keepPublishedText: string = 'Keep published';
  export let unassignConfirmTitle: string = 'Unassign this curation?';
  export let unassignConfirmWarningText: string = 'This will also cancel all scheduled email reminders for this curation.';
  export let keepAssignedText: string = 'Keep assigned';

  // Constants
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

   // Store / derived state
  $: modalName = $modalStore.modalName;
  $: publishedBool = publishedState === 'true';

  $: publishTitleText = publishText.replace('%{org_name}', orgName);
  $: publishSubtitleText = publishedBool ? publishSubtitleText.replace('%{org_name}', orgName) : prePublishSubtitleText.replace('%{org_name}', orgName);

  $: groupCountNum = Number(groupsCount) || 0;
  $: hasGroups = groupCountNum > 0;
  $: assignDisabled = publishedBool || !hasGroups;
  $: isAssigned = Boolean(assignedGroupId);

  // Local UI state
  let linkCopied = false;
  let linkCopyTimeout: number | undefined;
  let isUnassigning = false;

  // Actions
  const openPublishPreview = () => {
    modalStore.setName('previewCuration');
  };

  const openAssignCuration = () => {
    modalStore.setName('assignCuration');
  };

  const copyLink = async () => {
    if (!copyUrl) return;

    try {
      await navigator.clipboard.writeText(copyUrl);
      linkCopied = true;

      if (linkCopyTimeout) window.clearTimeout(linkCopyTimeout);
      linkCopyTimeout = window.setTimeout(() => {
        linkCopied = false;
      }, 2000);
    } catch (e) {
      console.error('Failed to copy link', e);
    }
  };

  const unassign = async () => {
    if (!unassignUrl || isUnassigning) return;

    isUnassigning = true;
    try {
      const resp = await fetch(unassignUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({}),
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        console.error('Unassign failed', data);
        return;
      }

      // Reload so the assignment badges, the reminders button ("View past reminders"),
      // and the now read-only panel all re-render server-side after unassigning cancels
      // the scheduled reminders (BIG-129).
      window.location.reload();
    } catch (e) {
      console.error('Unassign failed', e);
    } finally {
      isUnassigning = false;
    }
  };
</script>

{#if modalName === 'shareCuration'}
  <Modal modalTitle="Share curation" width="wide" showCloseIcon={true} showFooter={false} closeButtonClasses="modal__close-x--share">
    <div slot="modal-contents" class="share-modal">
      <!-- Assign to group -->
      <div class="share-row">
        <div class="share-left">
          <div class="share-icon"><Icon icon="UsersRound" size="medium" /></div>
          <div class="share-text">
            <div class="share-title">{assignToGroupText}</div>
            <div class="share-subtitle">
              {#if isAssigned}
                {assignedText}
              {:else if publishedBool}
                {publishedBoolText}
              {:else if !hasGroups}
                {noGroupsText}
              {:else}
                {assignedGroupText}
              {/if}
            </div>
          </div>
        </div>
        {#if isAssigned}
          <button
            id="curation-share-unassign-button"
            class="share-action share-action--primary"
            type="button"
            on:click={() => modalStore.setName('confirmUnassign')}
          >
            {unassignButtonText}
          </button>
        {:else}
          <button
            id="curation-share-assign-button"
            class="share-action"
            type="button"
            disabled={assignDisabled}
            on:click={openAssignCuration}
          >
            {assignButtonText}
          </button>
        {/if}
      </div>
      <!-- Publish/Unpublish  -->
      <div class="share-row">
        <div class="share-left">
          <div class="share-icon"><Icon icon="Globe2" size="medium" /></div>
          <div class="share-text">
            <div class="share-title">{publishTitleText}</div>
            <div class="share-subtitle">{publishSubtitleText}</div>
          </div>
        </div>
        {#if publishedBool}
          <!-- Unpublish opens a confirmation modal first (BIG-129) -->
          <button
            id="curation-share-unpublish-button"
            class="share-action share-action--primary"
            type="button"
            on:click={() => modalStore.setName('confirmUnpublish')}
          >
            {unpublishButtonText}
          </button>
        {:else}
          <!-- Publish = open preview modal -->
          <button id="curation-share-publish-button" class="share-action" type="button" on:click={openPublishPreview} disabled={isAssigned}>
            {publishButtonText}
          </button>
        {/if}
      </div>
      <!-- Copy link -->
      <div class="share-row">
        <div class="share-left">
          <div class="share-icon"><Icon icon="Link" size="medium" /></div>
          <div class="share-text">
            <div class="share-title">{copyLinkButtonText}</div>
            <div class="share-subtitle">{copyLinkSubtitleText}</div>
          </div>
        </div>
        <button id="curation-share-copy-link-button" class="share-action {linkCopied ? 'share-action--primary' : ''}" type="button" on:click={copyLink}>
          {#if linkCopied}
            <span class="share-action__inner">
              Copied <Icon icon="Check" size="small" />
            </span>
          {:else}
            {copyLinkButtonText}
          {/if}
        </button>
      </div>
    </div>
  </Modal>
{/if}

{#if modalName === 'confirmUnpublish'}
  <Modal modalTitle={unpublishConfirmTitle} width="medium" showCloseIcon={true} showFooter={false}>
    <div slot="modal-contents" class="confirm-unpublish">
      <div class="confirm-warning">
        <span class="confirm-warning__icon"><Icon icon="TriangleAlert" size="medium" /></span>
        <div class="confirm-warning__text">
          <div class="confirm-warning__label">{warningLabel}</div>
          <div class="confirm-warning__body">{unpublishConfirmWarningText}</div>
        </div>
      </div>
      <div class="confirm-ctas">
        <button type="button" class="share-action" on:click={() => modalStore.setName('shareCuration')}>
          {keepPublishedText}
        </button>
        <!-- Confirm keeps the existing Rails PUT unpublish flow -->
        <form method="post" action={unpublishUrl} class="share-action-form">
          <input type="hidden" name="authenticity_token" value={csrfToken} />
          <input type="hidden" name="_method" value="put" />
          <button class="share-action share-action--primary" type="submit">
            {unpublishButtonText}
          </button>
        </form>
      </div>
    </div>
  </Modal>
{/if}

{#if modalName === 'confirmUnassign'}
  <Modal modalTitle={unassignConfirmTitle} width="medium" showCloseIcon={true} showFooter={false}>
    <div slot="modal-contents" class="confirm-unpublish">
      <div class="confirm-warning">
        <span class="confirm-warning__icon"><Icon icon="TriangleAlert" size="medium" /></span>
        <div class="confirm-warning__text">
          <div class="confirm-warning__label">{warningLabel}</div>
          <div class="confirm-warning__body">{unassignConfirmWarningText}</div>
        </div>
      </div>
      <div class="confirm-ctas">
        <button type="button" class="share-action" on:click={() => modalStore.setName('shareCuration')}>
          {keepAssignedText}
        </button>
        <!-- Confirm runs the existing AJAX unassign, then reloads -->
        <button type="button" class="share-action share-action--primary" on:click={unassign} disabled={isUnassigning}>
          {unassignButtonText}
        </button>
      </div>
    </div>
  </Modal>
{/if}

<style>
  .share-modal {
    display: flex;
    flex-direction: column;
    gap: theme('spacing.5');
  }

  .share-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: theme('spacing.4');
    @media (max-width: theme('screens.sm')) {
      flex-direction: column;
    }
  }

  .share-left {
    display: flex;
    align-items: flex-start;
    gap: theme('spacing.3');
    flex: 1;
  }

  .share-icon {
    flex: 0 0 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 2px;
  }

  .share-text {
    display: flex;
    flex-direction: column;
    gap: theme('spacing.2');
  }

  .share-title {
    font-weight: 500;
  }

  .share-subtitle {
    font-weight: 400;
    font-size: theme('fontSize.base');
  }

  .share-action-form {
    margin: 0;
    @media (max-width: theme('screens.sm')) {
      align-self: flex-end;
    }
  }

  .share-action {
    padding: 10px theme('spacing.6');
    border: 1px solid theme('colors.neutral.300');
    border-radius: theme('borderRadius.lg');
    background: theme('colors.white');
    font-weight: 500;
    cursor: pointer;
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.sm');
    @media (max-width: theme('screens.sm')) {
      align-self: flex-end;
    }
    &:hover {
      background: theme('colors.neutral.100');
    }
  }

  .share-action--primary {
    background: theme('colors.black');
    color: theme('colors.white');
    border-color: theme('colors.black');
    &:hover {
      background: theme('colors.neutral.800');
    }
  }

  .share-action:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: theme('colors.neutral.200');
    color: theme('colors.neutral.400');
    border: 1px solid transparent;
  }
  .share-action__inner {
    display: inline-flex;
    align-items: center;
    gap: theme('spacing.2');
  }

  /* ------------- Unpublish confirmation ------------- */
  .confirm-unpublish {
    display: flex;
    flex-direction: column;
    gap: theme('spacing.6');
  }

  .confirm-warning {
    display: flex;
    align-items: flex-start;
    gap: theme('spacing.3');
    background: theme('colors.pale-yellow');
    border: 1px solid theme('colors.yellow');
    border-radius: theme('borderRadius.lg');
    padding: theme('padding.4');
  }

  .confirm-warning__icon {
    display: flex;
    flex-shrink: 0;
    color: theme('colors.amber.500');
  }

  .confirm-warning__label {
    font-weight: 500;
    margin-bottom: theme('spacing.1');
  }

  .confirm-warning__body {
    font-size: theme('fontSize.sm');
    color: theme('colors.neutral.500');
  }

  .confirm-ctas {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: theme('spacing.3');
  }
  /* ------------------ MOBILE ------------------ */
@media (max-width: 639px) {
  .share-modal {
    padding: theme('spacing.6');
    gap: theme('spacing.6');
  }
  .share-left {
    min-width: 0;
  }
  .share-text {
    min-width: 0;
  }
  .share-icon {
    margin-top: theme('spacing.1');
  }
  .share-title {
    margin-bottom: 0;
  }
  .share-subtitle {
    line-height: theme('lineHeight.normal');
    overflow-wrap: anywhere;
  }
}
</style>
